import { inject, Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { optimisticUpdate, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { Action, Store } from '@ngrx/store';
import { undo } from 'ngrx-undo-v2';
import { EMPTY, from, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { DalActions, DalState } from '..';
import { ClassGroupInterface, GroupInterface, PersonPreferenceInterface, PreferenceKey } from '../../+models';
import { PERSON_PREFERENCE_SERVICE_TOKEN, PersonPreferenceServiceInterface } from '../../person-preference';
import { EffectFeedback, EffectFeedbackActions } from '../effect-feedback';
import { AddEffectFeedback } from '../effect-feedback/effect-feedback.actions';
import { UserActions } from '../user';
import {
  AddPersonPreference,
  DeletePersonPreference,
  PersonPreferencesActionTypes,
  StartAddPersonPreference,
  StartAddTemplate,
  StartUpsertPersonPreference,
  UpsertPersonPreference,
} from './person-preference.actions';
import { GroupQueries } from '../group';
import { PersonPerRoleQueries } from '../linked-person';
import { Dictionary } from '@ngrx/entity';

@Injectable()
export class PersonPreferenceEffects {
  protected store: Store<DalState> = inject(Store);

  startAddPersonPreference$ = createEffect(() =>
    this.actions.pipe(
      ofType(PersonPreferencesActionTypes.StartAddPersonPreference),
      pessimisticUpdate({
        run: (action: StartAddPersonPreference) => {
          const { userId, personPreference } = action.payload;
          return this.personPreferenceService.createPersonPreference(userId, personPreference).pipe(
            switchMap((newPersonPreference: PersonPreferenceInterface) => {
              const actionsToDispatch: Action[] = [new AddPersonPreference({ personPreference: newPersonPreference })];
              if (newPersonPreference.key === 'ALTERNATIVE_PLATFORM_USAGE') {
                actionsToDispatch.push(new UserActions.StartUpdateCompleteProfile());
              }

              return from(actionsToDispatch);
            })
          );
        },
        onError: (action: StartAddPersonPreference, error) => {
          return new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Het is niet gelukt om de voorkeur op te slaan.'
            ),
          });
        },
      })
    )
  );

  deletePersonPreference$ = createEffect(() =>
    this.actions.pipe(
      ofType(PersonPreferencesActionTypes.DeletePersonPreference),
      optimisticUpdate({
        run: (action: DeletePersonPreference) => {
          return this.personPreferenceService.deletePersonPreference(action.payload.id).pipe(
            map(() => {
              if (action.payload.updateCompleteProfile) {
                return new UserActions.StartUpdateCompleteProfile();
              }
              return new DalActions.ActionSuccessful({ successfulAction: '[PersonPreference] deleted' });
            })
          );
        },
        undoAction: (action: DeletePersonPreference, error) => {
          const undoAction = undo(action);
          const effectFeedback = EffectFeedback.generateErrorFeedback(
            this.uuid(),
            action,
            'Het is niet gelukt om de voorkeur te verwijderen.'
          );
          const effectFeedbackAction = new EffectFeedbackActions.AddEffectFeedback({ effectFeedback });
          return from([undoAction, effectFeedbackAction]);
        },
      })
    )
  );

  startUpsertPersonPreference$ = createEffect(() =>
    this.actions.pipe(
      ofType(PersonPreferencesActionTypes.StartUpsertPersonPreference),
      withLatestFrom(
        this.store.select(PersonPerRoleQueries.getClassGroupsByStudentId),
        this.store.select(GroupQueries.getGroupsByStudentId)
      ),
      switchMap(
        ([action, classGroups, groups]: [
          StartUpsertPersonPreference,
          Dictionary<ClassGroupInterface[]>,
          Dictionary<GroupInterface[]>,
        ]) => {
          const { personPreference, noFeedBack } = action.payload;
          let updatedPersonPreference = { ...personPreference };
          if (updatedPersonPreference.key === PreferenceKey.GLOBAL_ASSIGNEE_FILTER) {
            updatedPersonPreference = {
              ...updatedPersonPreference,
              value: {
                ...updatedPersonPreference.value,
                linkedClassgroupIds: updatedPersonPreference.value.studentIds.flatMap(
                  (studentId) => classGroups[studentId]?.map((classGroup) => classGroup.id) || []
                ),
                linkedGroupIds: updatedPersonPreference.value.studentIds.flatMap(
                  (studentId) => groups[studentId]?.map((group) => group.id) || []
                ),
              },
            };
          }

          return this.personPreferenceService.upsertPersonPreference(updatedPersonPreference).pipe(
            switchMap((upsertedPersonPreference) => {
              const actions: Action[] = [new UpsertPersonPreference({ personPreference: upsertedPersonPreference })];

              if (!noFeedBack) {
                actions.push(
                  new AddEffectFeedback({
                    effectFeedback: EffectFeedback.generateSuccessFeedback(
                      this.uuid(),
                      action,
                      'Voorkeur is aangepast.'
                    ),
                  })
                );
              }

              return actions;
            }),
            catchError(() => {
              if (noFeedBack) return EMPTY;

              return of(
                new AddEffectFeedback({
                  effectFeedback: EffectFeedback.generateErrorFeedback(
                    this.uuid(),
                    action,
                    'Het is niet gelukt om de voorkeur aan te passen.'
                  ),
                })
              );
            })
          );
        }
      )
    )
  );

  startAddTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(PersonPreferencesActionTypes.StartAddTemplate),
      pessimisticUpdate({
        run: (action: StartAddTemplate) => {
          const { personPreference } = action.payload;

          return this.personPreferenceService.createTemplate(personPreference).pipe(
            switchMap((created) =>
              from([
                new AddPersonPreference({ personPreference: created }),
                new AddEffectFeedback({
                  effectFeedback: EffectFeedback.generateSuccessFeedback(
                    this.uuid(),
                    action,
                    'Het sjabloon werd toegevoegd.'
                  ),
                }),
              ])
            )
          );
        },
        onError: (action: StartAddTemplate, error) => {
          return new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Het is niet gelukt om het sjabloon toe te voegen.'
            ),
          });
        },
      })
    )
  );

  constructor(
    @Inject('uuid') private uuid: () => string,
    private actions: Actions,
    @Inject(PERSON_PREFERENCE_SERVICE_TOKEN)
    private personPreferenceService: PersonPreferenceServiceInterface
  ) {}
}
